import { type FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  roleHasPermissions,
  companySizeSchema,
  industrySchema,
  REGION_PRESENTATION,
  type CompanySize,
  type Industry,
} from '@lizrd/contracts';
import type { MfaPolicy, UpdatePrivacySettingsInput } from '@lizrd/contracts';
import {
  getOrganization,
  updateOrganization,
  setMfaPolicy,
  setPrivacySettings,
  enableNameTokenization,
  deleteAccount,
  exportOrganizationData,
} from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Toggle } from '../components/ui/toggle';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { useToast } from '../components/ui/toast';

const SIZES = companySizeSchema.options;
const INDUSTRIES = industrySchema.options;

export function SettingsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role ? roleHasPermissions(role, [Permission.OrgManage]) : false;
  const qc = useQueryClient();
  const { toast } = useToast();
  const org = useQuery({ queryKey: ['organization'], queryFn: getOrganization });

  const policyMut = useMutation({
    mutationFn: (mfaPolicy: MfaPolicy) => setMfaPolicy(mfaPolicy),
    onSuccess: (updated) => {
      qc.setQueryData(['organization'], updated);
      toast({
        title:
          updated.mfaPolicy === 'REQUIRED'
            ? 'Two-factor authentication is now required'
            : 'Two-factor authentication is now optional',
        tone: 'success',
      });
    },
    onError: (e) =>
      toast({
        title: 'Could not update the policy',
        description: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      }),
  });
  const privacyMut = useMutation({
    mutationFn: (input: UpdatePrivacySettingsInput) => setPrivacySettings(input),
    onSuccess: (res) => {
      qc.setQueryData(['organization'], res.organization);
      const p = res.purged;
      // Compose a summary from whichever purge(s) actually ran.
      const parts: string[] = [];
      if (p && (p.sourceSnippetsStripped || p.explanationsDeleted || p.recommendationsCleared)) {
        parts.push(
          `purged ${p.sourceSnippetsStripped} stored source snippet set(s), ${p.explanationsDeleted} explanation(s), and cleared ${p.recommendationsCleared} cached recommendation(s)`,
        );
      }
      if (p && p.aiEdgesRemoved) {
        parts.push(`removed ${p.aiEdgesRemoved} AI-matched resource→code link(s)`);
      }
      const summary = parts.join('; ');
      toast({
        title: 'Privacy settings updated',
        description: summary ? `${summary.charAt(0).toUpperCase()}${summary.slice(1)}.` : undefined,
        tone: 'success',
      });
    },
    onError: (e) =>
      toast({
        title: 'Could not update privacy settings',
        description: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      }),
    // Keep the confirm modal open (with its spinner) until the purge finishes.
    onSettled: () => {
      setConfirmSourceOff(false);
      setConfirmAiOff(false);
    },
  });
  const [form, setForm] = useState({ name: '', industry: '', companySize: '', websiteUrl: '' });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [confirmSourceOff, setConfirmSourceOff] = useState(false);
  const [confirmAiOff, setConfirmAiOff] = useState(false);
  // Terminal name-tokenization arm — its own modal with a type-the-org-name gate.
  const [tokenizeOpen, setTokenizeOpen] = useState(false);
  const [tokenizeConfirm, setTokenizeConfirm] = useState('');
  const [tokenizeAck, setTokenizeAck] = useState(false);
  const tokenizeMut = useMutation({
    mutationFn: () => enableNameTokenization(tokenizeConfirm.trim()),
    onSuccess: (res) => {
      qc.setQueryData(['organization'], res.organization);
      const t = res.tokenized;
      toast({
        title: 'Resource names are now tokenized',
        description: `Re-hashed ${t.nodesRehashed} resource name(s) and ${t.recommendationsRehashed} recommendation(s); dropped ${t.explanationsDeleted} cached explanation(s). This is permanent.`,
        tone: 'success',
      });
      setTokenizeOpen(false);
      setTokenizeConfirm('');
      setTokenizeAck(false);
    },
    onError: (e) =>
      toast({
        title: 'Could not enable name tokenization',
        description: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      }),
  });
  const exportData = useMutation({
    mutationFn: exportOrganizationData,
    onSuccess: (data) => {
      // Download the bundle as a JSON file (GDPR Art. 15/20 portability).
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lizrd-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Your data export has downloaded', tone: 'success' });
    },
    onError: (e) =>
      toast({
        title: 'Could not export your data',
        description: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      }),
  });
  const del = useMutation({
    mutationFn: () => deleteAccount(confirmName.trim()),
    onSuccess: () => {
      // Account is gone — end the session and hard-navigate to a goodbye screen
      // (a full reload tears down all in-memory state) instead of silently
      // dead-ending on the login page.
      useAuthStore.setState({ user: null });
      window.location.href = '/account-deleted';
    },
    onError: (e) =>
      toast({
        title: 'Could not delete the account',
        description: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      }),
  });

  useEffect(() => {
    if (org.data) {
      setForm({
        name: org.data.name,
        industry: org.data.industry ?? '',
        companySize: org.data.companySize ?? '',
        websiteUrl: org.data.websiteUrl ?? '',
      });
    }
  }, [org.data]);

  const save = useMutation({
    mutationFn: () =>
      updateOrganization({
        name: form.name.trim(),
        industry: form.industry ? (form.industry as Industry) : null,
        companySize: form.companySize ? (form.companySize as CompanySize) : null,
        websiteUrl: form.websiteUrl.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to save'),
  });

  function update(field: keyof typeof form) {
    return (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      setSaved(false);
    };
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    save.mutate();
  }

  const fieldClass = 'block text-sm';
  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-muted';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Organization settings</h1>
        <p className="text-sm text-muted">Your organization's profile.</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {saved && <Alert variant="success">Saved.</Alert>}

      <Card className="p-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <label className={fieldClass}>
            <span className={labelClass}>Name</span>
            <Input required value={form.name} onChange={update('name')} disabled={!canManage} />
          </label>
          <label className={fieldClass}>
            <span className={labelClass}>Industry</span>
            <Select
              value={form.industry}
              onChange={(v) => setForm((f) => ({ ...f, industry: v }))}
              options={[
                { value: '', label: 'Not set' },
                ...INDUSTRIES.map((i) => ({ value: i, label: i })),
              ]}
              disabled={!canManage}
              ariaLabel="Industry"
            />
          </label>
          <label className={fieldClass}>
            <span className={labelClass}>Company size</span>
            <Select
              value={form.companySize}
              onChange={(v) => setForm((f) => ({ ...f, companySize: v }))}
              options={[
                { value: '', label: 'Not set' },
                ...SIZES.map((s) => ({ value: s, label: s })),
              ]}
              disabled={!canManage}
              ariaLabel="Company size"
            />
          </label>
          <label className={fieldClass}>
            <span className={labelClass}>Website</span>
            <Input
              type="url"
              placeholder="https://example.com"
              value={form.websiteUrl}
              onChange={update('websiteUrl')}
              disabled={!canManage}
            />
          </label>
          {canManage ? (
            <Button type="submit" disabled={save.isPending || !form.name.trim()}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          ) : (
            <p className="text-sm text-muted">Only administrators can edit these settings.</p>
          )}
        </form>
      </Card>

      {/* Data residency — the region this org is hosted in (docs/tech/07). Read-only:
          chosen at signup, immutable after. */}
      {org.data && (
        <Card className="space-y-2 p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-bone">Data residency</h2>
              <p className="text-sm text-muted">Where this organization&rsquo;s data is stored.</p>
            </div>
            <Badge variant="neutral">
              {REGION_PRESENTATION[org.data.region].flag}{' '}
              {REGION_PRESENTATION[org.data.region].short}
            </Badge>
          </div>
          <p className="max-w-xl text-sm text-bone">
            Hosted in the{' '}
            <b className="font-semibold">
              {REGION_PRESENTATION[org.data.region].label} (
              {REGION_PRESENTATION[org.data.region].location})
            </b>{' '}
            region — your data stays here and never leaves it. The region is chosen at signup and
            can&rsquo;t be changed afterwards.
          </p>
        </Card>
      )}

      {/* Security — org MFA enforcement policy (Feature 25, Phase B). */}
      <Card className="space-y-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-bone">Security</h2>
            <p className="text-sm text-muted">
              Authentication requirements for everyone in the org.
            </p>
          </div>
          {org.data &&
            (org.data.mfaPolicy === 'OPTIONAL' ? (
              <Badge variant="neutral">2FA optional</Badge>
            ) : (
              <Badge variant="success" dot>
                2FA required
              </Badge>
            ))}
        </div>

        <label className={fieldClass}>
          <span className={labelClass}>Two-factor authentication</span>
          <Select
            value={org.data?.mfaPolicy === 'OPTIONAL' ? 'OPTIONAL' : 'REQUIRED'}
            onChange={(v) => policyMut.mutate(v as MfaPolicy)}
            options={[
              { value: 'OPTIONAL', label: 'Optional — members choose' },
              { value: 'REQUIRED', label: 'Required — everyone must enroll' },
            ]}
            disabled={!canManage || policyMut.isPending}
            ariaLabel="Two-factor authentication policy"
          />
        </label>
        <p className="max-w-xl text-xs leading-relaxed text-muted">
          When required, any member signing in with a password must set up an authenticator app
          before they can use Lizrd. Members who sign in with Google or GitHub are already covered
          by their provider.
          {!canManage && ' Only administrators can change this.'}
        </p>
      </Card>

      {/* Privacy — opt-in data-minimization controls (admins only). */}
      {canManage && org.data && (
        <Card className="space-y-5 p-6">
          <div>
            <h2 className="font-display text-lg font-bold text-bone">Privacy</h2>
            <p className="max-w-xl text-sm text-muted">
              Data-minimization controls for privacy-sensitive organizations. Defaults keep full
              functionality; turning a control off reduces what Lizrd stores and sends to the AI
              provider.
            </p>
          </div>

          <Alert variant="warning">
            <span className="text-sm">
              <strong className="text-bone">A deliberate trade-off.</strong> The less context Lizrd
              can see, the less precise its work: with these turned off, some recommendations — and
              especially the step-by-step “Guide me” fixes — become more general and less tailored
              to your code. Only restrict what your organization needs to.
            </span>
          </Alert>

          <div className="flex items-start justify-between gap-4">
            <div className="max-w-xl">
              <span className="text-sm font-semibold text-bone">Ingest source code</span>
              <p className="mt-0.5 text-xs leading-relaxed text-muted">
                When off, Lizrd never stores your code snippets or fetches source to ground “Guide
                me” diffs (guidance stays prose-only). Turning this off also{' '}
                <strong className="text-bone">purges already-stored</strong> source snippets and
                source-grounded AI content for your org.
              </p>
            </div>
            <Toggle
              checked={org.data.ingestSourceEnabled}
              disabled={privacyMut.isPending}
              label="Ingest source code"
              onChange={(next) => {
                // Turning OFF is destructive (triggers a purge) → confirm via a modal.
                // Turning ON is safe → apply immediately.
                if (!next) setConfirmSourceOff(true);
                else privacyMut.mutate({ ingestSourceEnabled: true });
              }}
            />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="max-w-xl">
              <span className="text-sm font-semibold text-bone">AI resource correlation</span>
              <p className="mt-0.5 text-xs leading-relaxed text-muted">
                When off, Lizrd skips the AI step that matches resources to code by name (it sends
                resource names to the AI provider). Rule-based correlation keeps working. Turning
                this off also <strong className="text-bone">removes the AI-matched links</strong>{' '}
                already in your graph.
              </p>
            </div>
            <Toggle
              checked={org.data.aiCorrelationEnabled}
              disabled={privacyMut.isPending}
              label="AI resource correlation"
              onChange={(next) => {
                // Turning OFF is destructive (removes AI-matched edges) → confirm.
                // Turning ON is safe → apply immediately.
                if (!next) setConfirmAiOff(true);
                else privacyMut.mutate({ aiCorrelationEnabled: true });
              }}
            />
          </div>

          {/* Resource-name tokenization — TERMINAL + irreversible, so it's set apart
              as a destructive one-time arm (not a toggle). */}
          <div className="rounded-lg border border-danger/30 bg-danger/[0.03] p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-xl">
                <span className="text-sm font-semibold text-bone">Tokenize resource names</span>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">
                  Replace every resource <strong className="text-bone">name</strong> with a
                  Lizrd-generated pseudonym (e.g. <code>res_7f3a9c2e</code>) so the real names never
                  reach Lizrd. Recommendations will identify resources by pseudonym — you&rsquo;ll
                  locate them in your own cloud console by their resource ID.{' '}
                  <strong className="text-danger">This is permanent and cannot be undone</strong>:
                  existing names are irreversibly purged, and Lizrd support will no longer be able
                  to help you by resource name.
                </p>
              </div>
              {org.data.tokenizeResourceNames ? (
                <span className="shrink-0 self-center rounded-full border border-danger/40 bg-danger/[0.06] px-3 py-1 text-xs font-semibold text-danger">
                  Enabled · permanent
                </span>
              ) : (
                <Button
                  variant="danger"
                  className="shrink-0"
                  onClick={() => {
                    setTokenizeConfirm('');
                    setTokenizeAck(false);
                    setTokenizeOpen(true);
                  }}
                >
                  Enable…
                </Button>
              )}
            </div>
          </div>

          <ConfirmDialog
            open={confirmSourceOff}
            tone="danger"
            title="Turn off source ingestion?"
            description="Lizrd will stop storing and fetching your source code. It will also permanently delete the source snippets and source-grounded AI content it already holds for your organization — this can't be undone."
            confirmLabel={privacyMut.isPending ? 'Purging…' : 'Turn off & purge'}
            pending={privacyMut.isPending}
            onConfirm={() => privacyMut.mutate({ ingestSourceEnabled: false })}
            onCancel={() => setConfirmSourceOff(false)}
          />

          <ConfirmDialog
            open={confirmAiOff}
            tone="danger"
            title="Turn off AI correlation?"
            description="Lizrd will stop sending resource names to the AI provider for matching. It will also permanently delete the AI-matched resource→code links already in your graph — this can't be undone. Rule-based links are kept."
            confirmLabel={privacyMut.isPending ? 'Removing…' : 'Turn off & remove links'}
            pending={privacyMut.isPending}
            onConfirm={() => privacyMut.mutate({ aiCorrelationEnabled: false })}
            onCancel={() => setConfirmAiOff(false)}
          />

          {tokenizeOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => !tokenizeMut.isPending && setTokenizeOpen(false)}
            >
              <Card
                className="w-full max-w-md space-y-4 border-danger/40 p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="font-display text-lg font-bold text-danger">
                  Tokenize resource names?
                </h2>
                <p className="text-sm leading-relaxed text-haze">
                  This <strong>cannot be undone.</strong> Lizrd will permanently replace every
                  stored resource name with a pseudonym and purge the originals. From now on
                  you&rsquo;ll see names like <code>res_7f3a9c2e</code> and must find resources in
                  your own cloud console by their ID. Lizrd support will no longer be able to help
                  you by name.
                </p>
                <label className="block text-sm">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                    Type your organization name to confirm: <strong>{org.data?.name}</strong>
                  </span>
                  <Input
                    value={tokenizeConfirm}
                    onChange={(e) => setTokenizeConfirm(e.target.value)}
                    placeholder={org.data?.name}
                    autoFocus
                  />
                </label>
                <label className="flex items-start gap-2 text-sm text-haze">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={tokenizeAck}
                    onChange={(e) => setTokenizeAck(e.target.checked)}
                  />
                  <span>I understand this is permanent and cannot be reversed.</span>
                </label>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setTokenizeOpen(false)}
                    disabled={tokenizeMut.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => tokenizeMut.mutate()}
                    disabled={
                      tokenizeMut.isPending ||
                      !tokenizeAck ||
                      tokenizeConfirm.trim().toLowerCase() !== (org.data?.name ?? '').toLowerCase()
                    }
                  >
                    {tokenizeMut.isPending ? 'Tokenizing…' : 'Permanently tokenize'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </Card>
      )}

      {/* Your data — GDPR export (admins only). */}
      {canManage && (
        <Card className="space-y-3 p-6">
          <div>
            <h2 className="font-display text-lg font-bold text-bone">Your data</h2>
            <p className="max-w-xl text-sm text-muted">
              Download a machine-readable copy of the personal data Lizrd holds for your
              organization — profiles, members, billing metadata, activity, and more. Secrets and
              credentials are never included.
            </p>
          </div>
          <div>
            <Button
              variant="outline"
              disabled={exportData.isPending}
              onClick={() => exportData.mutate()}
            >
              {exportData.isPending ? 'Preparing…' : 'Export your data'}
            </Button>
          </div>
        </Card>
      )}

      {/* Danger zone — irreversible account deletion (admins only), at the very bottom. */}
      {canManage && (
        <Card className="space-y-3 border-danger/40 bg-danger/[0.03] p-6">
          <div>
            <h2 className="font-display text-lg font-bold text-danger">Danger zone</h2>
            <p className="max-w-xl text-sm text-muted">
              Permanently delete your account. This deactivates your organization, disconnects every
              integration, and cannot be undone. We&rsquo;ll issue a final bill for this
              month&rsquo;s cloud spend. Signing up again later starts a brand-new account.
            </p>
          </div>
          <div>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmName('');
                setDangerOpen(true);
              }}
            >
              Delete account…
            </Button>
          </div>
        </Card>
      )}

      {dangerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !del.isPending && setDangerOpen(false)}
        >
          <Card
            className="w-full max-w-md space-y-4 border-danger/40 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-lg font-bold text-danger">Delete this account?</h2>
            <p className="text-sm leading-relaxed text-haze">
              This <strong>cannot be undone</strong>. Your organization is deactivated, all
              integrations are disconnected, and everyone loses access. We&rsquo;ll bill any
              outstanding cloud spend for the current month.
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                Type your organization name to confirm: <strong>{org.data?.name}</strong>
              </span>
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={org.data?.name}
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDangerOpen(false)}
                disabled={del.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => del.mutate()}
                disabled={
                  del.isPending ||
                  confirmName.trim().toLowerCase() !== (org.data?.name ?? '').toLowerCase()
                }
              >
                {del.isPending ? 'Deleting…' : 'Permanently delete'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
